import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, CalendarDays, MessageCircle } from "lucide-react";
import { navLinks, business, routes, whatsappLink } from "@/lib/site";
import { useBooking } from "@/components/BookingContext";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openBooking } = useBooking();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl shadow-sm" : "bg-white"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[72px] items-center justify-between gap-4">
          <Link to={routes.home} className="flex items-center gap-3 shrink-0" data-testid="logo-link">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-navy text-white font-heading font-extrabold text-lg">
              S
            </span>
            <span className="leading-tight">
              <span className="block font-heading font-bold text-navy text-[15px] sm:text-base">
                {business.doctorName}
              </span>
              <span className="block text-[11px] sm:text-xs tracking-wide text-brand-orange font-semibold uppercase">
                Chiropractor · Varanasi
              </span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-semibold rounded-full whitespace-nowrap transition-colors ${
                    isActive ? "text-brand-orange" : "text-navy hover:text-brand-orange"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-2">
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-bold text-white whitespace-nowrap shadow-sm hover:brightness-95 hover:-translate-y-0.5 transition-[transform,filter] duration-200"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>

          <button
            className="xl:hidden grid h-11 w-11 place-items-center rounded-lg text-navy hover:bg-soft transition-colors"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-border bg-white" data-testid="mobile-nav">
          <nav className="mx-auto max-w-7xl px-4 py-3 flex flex-col">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `px-2 py-3 text-base font-semibold border-b border-border/60 ${
                    isActive ? "text-brand-orange" : "text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              onClick={openBooking}
              data-testid="mobile-book-btn"
              className="mt-4 mb-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-base font-bold text-white"
            >
              <CalendarDays className="h-5 w-5" /> Book Appointment
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
